import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import IcoDone from "@floji-internal/mui-icons/Done";
import IcoEdit from "@floji-internal/mui-icons/Edit";

import { ReturnGetTopic } from "--root/static_types/api_methods/methods.query";
import { CloudUserIdentity } from "--root/static_types/shared.types";
import { Strings } from "--root/strings/en";

import { SwaggerQueryClient } from "--client/common/apis/rest";
import { useCancellablePromiseEffect } from "--client/common/hooks/useCancellablePromiseEffect";

import { IconButton } from "--components/buttons/iconButton";
import ListEntryText from "--floponents-web/atoms/ListEntry/ListEntryText";
import { TextInput } from "--floponents-web/atoms/TextInput";
import { Body3 } from "--components/typography";

const DisplayNameSuggestions = styled.div`
    align-items: baseline;
    display: flex;
    gap: ${props => props.theme.spacing(1)};
    padding: ${props => props.theme.spacing(1)};
`;
const DisplayNameSuggestionsChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${props => props.theme.spacing(0.5)};
`;
const DisplayNameSuggestionsChip = styled.button`
    ${props => props.theme.typography.body2};
    background-color: ${props => props.theme.palette.primary.darker};
    border: none;
    border-radius: ${props => props.theme.radius.large};
    color: ${props => props.theme.palette.primary.contrast};
    cursor: pointer;
    margin: initial;
    padding: 0px ${props => props.theme.spacing(1)};
    user-select: none;
`;

interface DisplayNameEditorProps {
    stream: ReturnGetTopic;
    identities: CloudUserIdentity[];
    offnetId?: string;
    defaultName?: string;
    isEditing?: boolean;
    onEditStart?: () => void;
    onEditFinish?: (displayName: string | undefined) => void;
}

export const DisplayNameEditor = (props: DisplayNameEditorProps) => {
    const { onEditStart, onEditFinish } = props
    const [displayName, setDisplayName] = React.useState(props.defaultName);
    const textInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        setDisplayName(props.defaultName)
    }, [props.defaultName])

    const handleSave = useCallback(() => {
        onEditFinish?.(displayName);
    }, [displayName, onEditFinish])

    useLayoutEffect(() => {
        if (!props.defaultName) textInputRef.current?.focus()
    });

    // Display names found in other offnet users with matching contact info
    const [linkedDisplayNames, setLinkedDisplayNames] = useState<string[]>([]);

    // Linked display names not including current name
    const filteredLinkedDisplayNames = useMemo(() => {
        return linkedDisplayNames
            .filter(linkedDisplayName => linkedDisplayName !== displayName);
    }, [displayName, linkedDisplayNames]);

    useCancellablePromiseEffect(cxl => {
        if (props.stream.type.name !== "stream") return;

        // Only fetch linked display name identities if creating new offnet user
        if (props.offnetId) return;

        cxl.run(SwaggerQueryClient.GetLinkedOffnetDisplayNameIdentities({
            userspaceId: props.stream.type.userspace_id,
            identity: props.identities
        })).then(response => {
            setLinkedDisplayNames(response.map(identity => identity.value));
        });
    }, [props.identities, props.offnetId, props.stream.type]);

    const DefaultView: JSX.Element | null = (() => {
        if (props.isEditing || !props.defaultName) return null
        return <>
            <ListEntryText
                primary={
                    displayName ||
                    Strings.offnetControl.none
                }
                secondary={Strings.offnetControl.display_name}
            />
            <IconButton
                icon={<IcoEdit />}
                color="greyInverted"
                size="small"
                onClick={() => onEditStart?.()}
            />
        </>
    })()
    const EditView: JSX.Element | null = (() => {
        if (!props.isEditing && props.defaultName) return null
        return <>
            <form onSubmit={handleSave}>
                <TextInput
                    inputRef={textInputRef}
                    size="small"
                    autoFocus
                    label={Strings.offnetControl.display_name}
                    placeholder={Strings.offnetControl.display_name}
                    value={displayName}
                    onChange={(e, val) => setDisplayName(val)}
                />
            </form>
            <IconButton
                icon={<IcoDone />}
                color="positive"
                size="small"
                onClick={handleSave}
            />

            {!!filteredLinkedDisplayNames.length && (
                <DisplayNameSuggestions>
                    <Body3>{Strings.offnetControl.suggestions}</Body3>
                    <DisplayNameSuggestionsChips>
                        {filteredLinkedDisplayNames.map(linkedDisplayName => {
                            return (
                                <DisplayNameSuggestionsChip
                                    key={linkedDisplayName}
                                    onClick={() => setDisplayName(linkedDisplayName)}
                                >
                                    {linkedDisplayName}
                                </DisplayNameSuggestionsChip>
                            );
                        })}
                    </DisplayNameSuggestionsChips>
                </DisplayNameSuggestions>
            )}
        </>
    })()

    return <>
        {DefaultView}
        {EditView}
    </>
}
